import { getLdFlagsFromLocalStorage } from "@customer-app/LaunchDarkly/getLdFlagsFromLocalStorage";
import { PurchaseStateAndDelivery, isUpcomingDelivery } from "@smartrr/shared/entities/PurchaseState";
import { IPurchaseStateLineItem } from "@smartrr/shared/entities/PurchaseState/CustomerPurchaseLineItem";
import { IDiscount, IPurchaseStateDiscount } from "@smartrr/shared/interfaces/Discount";
import isNumber from 'lodash/isNumber';
import { v4 } from "uuid";

// TODO: Move to /shared and replace hardcoded strings in BE with enum
export enum DISCOUNT_CODE_TYPES {
  DISCOUNT = "DISCOUNT",
  REFERRER = "REFERRER",
  REFEREE = "REFEREE",
}

export const isDiscountSpecialReferrals = (discount: IDiscount) =>
  Object.keys(DISCOUNT_CODE_TYPES).some(key => discount.code?.startsWith(key));

export const applySpecialReferralsDiscountToDeliveries = (deliveries: PurchaseStateAndDelivery[]) => {
  const ldFlags = getLdFlagsFromLocalStorage();

  if (!ldFlags) {
    return deliveries;
  }
  const specialReferralsVariantId = ldFlags.specialReferrals?.value;

  if (!specialReferralsVariantId) {
    return deliveries;
  }

  return deliveries.reduce((acc, delivery) => {
    const lineItems = delivery.purchaseState.stLineItems;
    const { indexFromNext } = delivery;
    const cpsContainsSpecialVariant = lineItems.some(li => li.vnt.shopifyId === specialReferralsVariantId);

    if (!cpsContainsSpecialVariant) {
      acc.push(delivery);
      return acc;
    }
    const cps = delivery.purchaseState;
    const remainingSpecialRewards = cps.subProperties?.remainingSpecialRewards ?? 0;
    const cpsSpecialReferralsDiscount = cps.discounts.find(discount => isDiscountSpecialReferrals(discount));
    const isUpcoming = isUpcomingDelivery(indexFromNext);
    const isRemainingSpecialRewardsValid = isNumber(remainingSpecialRewards) && remainingSpecialRewards >= 0;

    // If upcoming order then discount is already applied and is valid
    // If future order, we need to ensure remainingSpecialRewards >= 0
    const deliveryHasSpecialRewards = isUpcoming
      ? true
      : isRemainingSpecialRewardsValid && indexFromNext <= remainingSpecialRewards;

    if (!cpsSpecialReferralsDiscount || !deliveryHasSpecialRewards) {
      acc.push(delivery);
      return acc;
    }

    // ensuring whatever additional discounts that were applied to cps remain
    const remainingDiscounts = cps.discounts.filter(
      discount => discount.code !== cpsSpecialReferralsDiscount?.code
    );

    // for those lines that have special referrals discount applied we want to simulate being a redeemed referral incentive item
    // doing so prevents users from being able to make updates to line (quantity, add, remove, swap)
    const updatedLines: IPurchaseStateLineItem[] = cps.stLineItems.reduce((prev, curr) => {
      const specialReferralReward = curr.discounts.find(discount => isDiscountSpecialReferrals(discount));
      const hasMultipleItems = curr.quantity > 1;

      if (specialReferralReward) {
        if (hasMultipleItems) {
          prev.push({
            ...curr,
            discounts: curr.discounts.filter(discount => discount.code !== specialReferralReward.code),
            quantity: curr.quantity - 1,
          });
        }

        prev.push({
          ...curr,
          id: v4(),
          quantity: hasMultipleItems ? 1 : curr.quantity,
          discounts: [specialReferralReward],
          isRedeemed: true,
        });

        return prev;
      }
      prev.push(curr);
      return prev;
    }, [] as IPurchaseStateLineItem[]);

    // incrementing recurringCycleLimit by how many remaining rewards the user has been given for referrals
    const updatedDiscount: IPurchaseStateDiscount = {
      ...cpsSpecialReferralsDiscount,
      recurringCycleLimit: remainingSpecialRewards + 1,
    };

    const newDelivery: PurchaseStateAndDelivery = {
      ...delivery,
      purchaseState: {
        ...delivery.purchaseState,
        discounts: [...remainingDiscounts, updatedDiscount],
        stLineItems: updatedLines,
      },
    };

    acc.push(newDelivery);
    return acc;
  }, [] as PurchaseStateAndDelivery[]);
};
